<template>
  <table v-if="filteredFeatures.length" class="structured-table w-full">
    <tbody>
      <tr v-for="(feature, index) in filteredFeatures" :key="index">
        <th class="md:w-1/3">{{ feature.label }}</th>
        <td v-html="feature.value" />
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import type {
  CommerceProductAttributeValueFragment,
  CommerceProductPageVariationFieldFragment,
  CommerceProductPageVariationFragment,
} from '#graphql-operations'
import type { Option } from '~/types/option'

import { falsy } from '#shared/helpers/type'

const { $texts } = useNuxtApp()

const props = defineProps<{
  variation: CommerceProductPageVariationFragment
  showCapacity?: boolean
}>()

interface CustomProductSpecification {
  key: string
  value: string
}

/**
 * Map a product variation attribute to a feature.
 */
function mapAttributeToFeature(
  attribute: CommerceProductAttributeValueFragment,
): Option {
  return {
    label: attribute.attribute?.label || '',
    value: attribute.name || '',
  }
}

/**
 * Map a generic field to a feature.
 */
function mapFieldToFeature(
  field: CommerceProductPageVariationFieldFragment | undefined | null,
): Option | undefined {
  if (!field?.value) {
    return
  }

  return {
    label:
      field.fieldConfig &&
      'label' in field.fieldConfig &&
      field.fieldConfig.label
        ? field.fieldConfig.label
        : '',
    value: field.value,
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const features = computed<Option[]>(() => {
  if (!props.variation) {
    return []
  }

  const features: (Option | undefined | null)[] = []

  if (props.variation.sku) {
    features.push({
      label: $texts('product_detail.sku'),
      value: props.variation.sku,
    })
  }
  if ('size' in props.variation && props.variation.size) {
    features.push(mapAttributeToFeature(props.variation.size))
  }
  if ('language' in props.variation && props.variation.language) {
    features.push(mapAttributeToFeature(props.variation.language))
  }
  if ('format' in props.variation) {
    features.push(mapFieldToFeature(props.variation.format))
  }
  if ('publisher' in props.variation) {
    features.push(mapFieldToFeature(props.variation.publisher))
  }
  if ('eventDate' in props.variation && props.variation.eventDate?.formatted) {
    features.push({
      label: $texts('eventDate'),
      value: props.variation.eventDate.formatted || '',
    })
  }
  if ('scopeOfSupply' in props.variation) {
    features.push({
      label: $texts('scopeOfSupply'),
      value: props.variation.scopeOfSupply?.name || '',
    })
  }

  if (
    'registration' in props.variation &&
    props.variation.registration?.settings
  ) {
    features.push({
      label: $texts('applicationStart'),
      value: props.variation.registration.settings.open?.formatted || '',
    })
    features.push({
      label: $texts('applicationDeadline'),
      value: props.variation.registration.settings.close?.formatted || '',
    })

    features.push({
      label: $texts('capacity'),
      value:
        (props.showCapacity
          ? props.variation.registration.settings.capacity?.toString()
          : '') || '',
    })
  }
  if ('location' in props.variation) {
    features.push({
      label: $texts('location'),
      value: props.variation.location || '',
    })
  }
  if ('organizer' in props.variation) {
    features.push({
      label: $texts('organizer'),
      value: props.variation.organizer || '',
    })
  }
  if ('availability' in props.variation) {
    features.push({
      label: $texts('availability'),
      value: props.variation.availability?.formatted?.toString() || '',
    })
  }
  if ('fphPoints' in props.variation) {
    features.push({
      label: $texts('fphPoints'),
      value: props.variation.fphPoints?.toString() || '',
    })
  }
  if ('duration' in props.variation) {
    features.push({
      label: $texts('duration'),
      value: props.variation.duration?.toString() || '',
    })
  }

  if (
    'customSpecifications' in props.variation &&
    props.variation.customSpecifications?.list
  ) {
    // Output each custom specification as a feature.
    for (const customSpecification of props.variation.customSpecifications
      .list as CustomProductSpecification[]) {
      features.push({
        label: customSpecification.key || '',
        value: customSpecification.value || '',
      })
    }
  }

  return features.filter(falsy)
})

const filteredFeatures = computed<Option[]>(() => {
  return features.value.filter((feature) => feature.value)
})
</script>
